<template>
    <div class="mobile-venue-booking-calendar-container">
        <scrollable-base-calendar
            :numOfViews="1"
            :additional-months="12"
            :calendar.sync="calendar"
            :styleOptions="calendarStyles"
            @dateClicked="handleDateClick"
            @dateHovered="handleDateHover"
            @dateUnhovered="handleDateUnhover"
        >
            <template #month-label="{ month }">
                <div class="month-event-label">
                    <p class="label">
                        {{ month.label }}
                    </p>
                    <p class="events">
                        {{ numberOfEvents(month.label) }}
                    </p>
                </div>
            </template>
            <template #day-content="{ day }">
                <div :style="markerStyles" class="color-circles">
                    <div
                        v-for="(marker, indx) in day.markers"
                        :key="indx"
                        :class="`${marker.value}-circle`"
                        :style="circleStyles(day, marker, indx)"
                    ></div>
                </div>
                <div
                    v-show="showBubble(day)"
                    @mouseleave="handleLeaveBubble(day)"
                    class="bubble"
                >
                    <div
                        v-for="(status, indx) in dayStatues(day)"
                        :key="indx"
                        @click="handleStatusClick(status)"
                        class="bubble-content"
                    >
                        <p :style="statusLenStyle(status.color)" class="status-len">
                            {{ status.len }}
                        </p>
                        <p :style="statusValueStyle(status.color)" class="status-value">
                            {{ status.value }}
                        </p>
                    </div>
                </div>
            </template>
        </scrollable-base-calendar>
    </div>
</template>
<script>
    import ScrollableBaseCalendar from '@/components/ui/calendars/ScrollableBaseCalendar.vue'
    import { MONTHS } from '@/constants/calendar'
    import useCheckScreen from '@/composables/useCheckScreen.js'
    import useFormattedData from '@/composables/useFormattedData.js'

    const URLS = {
        icons: {
            next: '/images/icons/next.svg',
            prev: '/images/icons/prev.svg'
        },
        redirect: {
            book: '/event/book-artist',
            advance: '/event/advance-book-artist',
            completed: '/booking-manager/completed-history',
            wrapUp: '/event/wrap-up-artist',
            invited: '/make-offer',
            artistBook: '/make-offer',
            artistAdvance: '/booking-manager/artist/advance',
            artistWrapUp: '/booking-manager/artist/wrap-up',
            artistCompleted: '/booking-manager/completed-history'
        }
    }

    export default {
        name: 'MobileVenueBookingCalendar',
        components: {
            ScrollableBaseCalendar
        },
        props: {
            events: {
                required: true,
                type: Array
            },
            artistCalendar: {
                required: false,
                type: Boolean,
                default: false
            },
            unavails: {
                required: false,
                type: Array,
                default: () => []
            },
            subscriptionWarning: {
                required: false,
                type: Boolean,
                default: false
            }
        },
        emits: ['formatted-date', 'show-modal', 'show-invite-modal', 'filter-events'],
        setup() {
            const { isSmallMobile, isMedSizeMobile } = useCheckScreen()
            const { resolveFormattedDate } = useFormattedData()

            return {
                resolveFormattedDate,

                screen: {
                    isSmallMobile,
                    isMedSizeMobile
                }
            }
        },
        data: () => ({
            calendar: [],
            eventLabels: [],
            originalEvents: [],
            styleOptions: {
                background: {
                    background: '#262626'
                },
                font: {
                    'font-family': 'Instrument Sans',
                    'font-size': '14px'
                },
                wrapper: {
                    gap: '5px'
                }
            }
        }),
        watch: {
            events: function (events, prevEvents) {
                if (events.length === prevEvents.length) {
                    this.originalEvents = []
                    this.clearCalendarMarkers()
                }
                if (events.length) this.initializeEvents()
            },
            calendar: function () {
                if (this.events.length) this.initializeEvents()
            }
        },
        computed: {
            URLS: () => URLS,
            calendarStyles: (self) => {
                if (self.screen.isSmallMobile.value) {
                    return self.smallMobileCalendarStyles
                } else if (
                    self.screen.isMedSizeMobile.value &&
                    !self.screen.isSmallMobile.value
                ) {
                    return self.medSizeMobileCalendarStyles
                } else return self.styleOptions
            },
            smallMobileCalendarStyles: (self) => ({
                ...self.styleOptions,
                background: {
                    background: '#262626',
                    width: '276px !important',
                    padding: '15px 10px 15px 10px'
                }
            }),
            medSizeMobileCalendarStyles: (self) => ({
                ...self.styleOptions,
                background: {
                    background: '#262626',
                    width: '320px !important'
                }
            }),
            markerStyles: (self) => {
                if (self.screen.isSmallMobile.value) {
                    return {
                        top: '22% !important',
                        right: '9% !important'
                    }
                } else if (self.screen.isMedSizeMobile.value) {
                    return {
                        top: '18% !important'
                    }
                }
            },
            circleStyles: () => (day, marker, indx) => {
                if (marker.value.includes('main')) return {}

                let mainCircle = day.markers.find((m) => m.value.includes('main'))
                let circleOffset = -2

                if (marker.value === 'cancelled') {
                    return { display: 'none' }
                } else if (indx < day.markers.indexOf(mainCircle)) {
                    let eventOffset = (indx + 1) * circleOffset
                    return { 'margin-left': `${eventOffset}px` }
                } else {
                    let eventOffset = indx * circleOffset
                    return { 'margin-left': `${eventOffset}px` }
                }
            },
            showBubble: () => (day) => {
                if (day.markers) {
                    let eventMarkers = day.markers.filter((m) => m.type === 'event')
                    return eventMarkers.length > 1 ? day.hover && day.showBubble : false
                }
                return false
            },
            dayStatues: () => (day) => {
                if (!day.markers || !day.markers.length) return

                let statues = []
                let eventStatues = []
                let dayEvents = day.markers.filter((m) => m.type === 'event')

                dayEvents.forEach((e) => {
                    switch (e.value) {
                        case 'availsChecked':
                        case 'main-availsChecked':
                            statues.push({
                                color: '#CDEB09',
                                url: e.url,
                                eventStatus: 0,
                                value: 'Avails Checked'
                            })
                            break
                        case 'book':
                        case 'main-book':
                            statues.push({
                                color: '#C9CD00',
                                url: e.url,
                                eventStatus: 1,
                                value: 'Book'
                            })
                            break
                        case 'advance':
                        case 'main-advance':
                            statues.push({
                                color: '#CB9B1F',
                                url: e.url,
                                eventStatus: 2,
                                value: 'Advance'
                            })
                            break
                        case 'wrapUp':
                        case 'main-wrapUp':
                            statues.push({
                                color: '#D188BF',
                                url: e.url,
                                eventStatus: 3,
                                value: 'Wrap Up'
                            })
                            break
                        case 'cancelled':
                        case 'main-cancelled':
                        case 'main-border-cancelled':
                            statues.push({
                                color: '#BA87FF',
                                url: e.url,
                                eventStatus: 5,
                                value: 'Cancelled'
                            })
                            break
                        case 'completed':
                            statues.push({
                                color: '#646464',
                                url: e.url,
                                eventStatus: 4,
                                value: 'Completed'
                            })
                            break
                        default:
                            break
                    }
                })

                statues.forEach((s) => {
                    let len = statues.filter((status) => status.value === s.value).length
                    let status = eventStatues.find((e) => e.value === s.value)
                    if (status) return

                    eventStatues.push({
                        ...s,
                        len: len
                    })
                })

                return eventStatues
            },
            statusLenStyle: () => (color) => ({
                color: color,
                'border-color': color
            }),
            statusValueStyle: () => (color) => ({
                color: color
            }),
            numberOfEvents: (self) => (label) => {
                let monthEvents = self.eventLabels.find((e) => e.label === label)

                if (!monthEvents || monthEvents.events.length === 0) return '0 Event'

                return `${monthEvents.events.length} Events`
            },

            allocatedBudget: (self) => (label) => {
                let allocated = 0
                let totalAllocated = 0
                let monthEvents = self.eventLabels.find((e) => e.label === label)

                if (!monthEvents) return ''

                for (let i = 0; i < monthEvents.events.length; i++) {
                    let event = monthEvents.events[i]

                    totalAllocated = totalAllocated + parseFloat(event.maxBudget)

                    if (!event.counter) {
                        allocated = allocated + parseFloat(event.minBudget)
                    } else {
                        allocated = allocated + parseFloat(event.counter)
                    }
                }

                return `${self.formatCurrency(allocated)} of ${self.formatCurrency(
                    totalAllocated
                )} Allocated`
            }
        },
        methods: {
            initializeEvents() {
                this.initEventLabels()
                this.markCurrentDay()

                if (this.artistCalendar) {
                    this.markUnavailDates()
                }

                const eventDays = this.markEventDays()
                this.markMainEvent(eventDays)
            },
            initEventLabels() {
                for (let i = 0; i < this.events.length; i++) {
                    let event = this.events[i]
                    let date = new Date(event.start)
                    let label = this.convertDateToLabel(date)

                    const markedLabel = this.eventLabels.find((e) => e.label === label)

                    if (markedLabel) {
                        const markedEventLabel = markedLabel.events.find(
                            (e) => e.eventId === event.eventId
                        )

                        if (!markedEventLabel)
                            this.eventLabels.find((e) => e.label === label).events.push(event)
                    } else {
                        this.eventLabels.push({
                            label: label,
                            events: [event]
                        })
                    }
                }
            },
            markCurrentDay() {
                let date = new Date()
                let day = this.convertDateToDayObj(date)

                if (!day.markers) {
                    this.$set(day, 'markers', [])
                }

                if (!day.markers.find((m) => m.value === 'current')) {
                    day.markers.push({
                        type: 'date',
                        value: 'current'
                    })
                    this.$set(day, 'today', true)
                }
            },
            markEventDays() {
                let eventDays = []

                for (let i = 0; i < this.events.length; i++) {
                    let event = this.events[i]
                    let date = new Date(event.startDate.replace(/-/g, '/'))

                    if (isNaN(date)) continue

                    const foundEvent = this.originalEvents.find(
                        (e) => e.eventId === event.eventId
                    )

                    if (!foundEvent) {
                        let day = this.convertDateToDayObj(date)

                        if (!day.markers) {
                            this.$set(day, 'markers', [])
                            eventDays.push(day)
                        }

                        if (day.artistNotAvail) {
                            continue
                        }

                        switch (event.title) {
                            case 'Invited':
                            case 'Avails Checked':
                                day.markers.push({
                                    type: 'event',
                                    url: this.artistCalendar
                                        ? this.URLS.redirect.artistBook
                                        : null,
                                    id: event.eventId,
                                    value: 'availsChecked',
                                    vibe: event.vibe,
                                    budget: event.budget,
                                    startDate: event.startDate,
                                    startTime: event.startTime,
                                    privateOpportunity: event.privateOpportunity
                                })
                                this.$set(
                                    day,
                                    'url',
                                    this.artistCalendar ? this.URLS.redirect.artistBook : null
                                )
                                this.$set(day, 'availsChecked', true)
                                break
                            case 'Book':
                                day.markers.push({
                                    type: 'event',
                                    url: this.artistCalendar
                                        ? this.URLS.redirect.artistBook
                                        : this.URLS.redirect.book,
                                    id: event.eventId,
                                    value: 'book'
                                })
                                this.$set(day, 'book', true)
                                this.$set(
                                    day,
                                    'url',
                                    this.artistCalendar
                                        ? this.URLS.redirect.artistBook
                                        : this.URLS.redirect.book
                                )
                                break
                            case 'Advance':
                                day.markers.push({
                                    type: 'event',
                                    url: this.artistCalendar
                                        ? this.URLS.redirect.artistAdvance
                                        : this.URLS.redirect.advance,
                                    id: event.eventId,
                                    value: 'advance'
                                })
                                this.$set(day, 'advance', true)
                                this.$set(
                                    day,
                                    'url',
                                    this.artistCalendar
                                        ? this.URLS.redirect.artistAdvance
                                        : this.URLS.redirect.advance
                                )
                                break
                            case 'WrapUp':
                                day.markers.push({
                                    type: 'event',
                                    url: this.artistCalendar
                                        ? this.URLS.redirect.artistWrapUp
                                        : this.URLS.redirect.wrapUp,
                                    id: event.eventId,
                                    value: 'wrapUp'
                                })
                                this.$set(day, 'wrapUp', true)
                                this.$set(
                                    day,
                                    'url',
                                    this.artistCalendar
                                        ? this.URLS.redirect.artistWrapUp
                                        : this.URLS.redirect.wrapUp
                                )
                                break
                            case 'Cancelled':
                                day.markers.push({
                                    type: 'event',
                                    value: 'cancelled'
                                })
                                this.$set(day, 'cancelled', true)
                                break
                            case 'Completed':
                                day.markers.push({
                                    type: 'event',
                                    url: this.artistCalendar
                                        ? `${this.URLS.redirect.artistCompleted}/${event.eventId}`
                                        : `${this.URLS.redirect.completed}/${event.eventId}`,
                                    id: event.eventId,
                                    value: 'completed'
                                })
                                this.$set(day, 'completed', true)
                                this.$set(
                                    day,
                                    'url',
                                    this.artistCalendar
                                        ? `${this.URLS.redirect.artistCompleted}/${event.eventId}`
                                        : `${this.URLS.redirect.completed}/${event.eventId}`
                                )
                                break
                            case 'Rejected':
                                day.markers.push({
                                    type: 'event',
                                    value: 'cancelled'
                                })
                                this.$set(day, 'cancelled', true)
                                break
                            default:
                                break
                        }
                    }
                }

                this.originalEvents = this.events

                return eventDays
            },
            markMainEvent(eventDays) {
                for (let i = 0; i < eventDays.length; i++) {
                    let event = eventDays[i]

                    let onlyEvent =
                        eventDays[i].markers.length === 1 &&
                        eventDays[i] != this.convertDateToDayObj(new Date())

                    let cancelledEvent = event.markers.find((m) => m.value === 'cancelled')

                    if (cancelledEvent && onlyEvent) {
                        cancelledEvent.value = 'main-' + cancelledEvent.value
                    } else if (cancelledEvent && !onlyEvent) {
                        cancelledEvent.value = 'main-border-' + cancelledEvent.value
                    } else {
                        let eventMarker = eventDays[i].markers[0]

                        eventMarker.value = 'main-' + eventMarker.value

                        this.disableEventMarkers(eventDays[i])

                        eventDays[i][eventMarker.value.replace('main-', '')] = true
                    }
                }
            },
            markUnavailDates() {
                for (let i = 0; i < this.unavails.length; i++) {
                    let unavail = this.unavails[i]
                    let date = new Date(unavail.date)
                    let day = this.convertDateToDayObj(date)

                    if (!day.markers) {
                        this.$set(day, 'markers', [])
                    }

                    let today = day.markers.find((m) => m.type === 'date')

                    if (today) continue

                    day.markers.push({
                        type: 'artistNotAvail',
                        value: 'artistNotAvail'
                    })
                    this.$set(day, 'artistNotAvail', true)
                }
            },
            clearCalendarMarkers() {
                this.calendar.forEach((month) => {
                    month.days.forEach((day) => {
                        this.disableEventMarkers(day)
                        day.markers = null
                    })
                })
            },
            disableEventMarkers(day) {
                if (day.availsChecked) {
                    day.availsChecked = false
                }
                if (day.book) {
                    day.book = false
                }
                if (day.advance) {
                    day.advance = false
                }
                if (day.wrapUp) {
                    day.wrapUp = false
                }
                if (day.cancelled) {
                    day.cancelled = false
                }
                if (day.completed) {
                    day.completed = false
                }
            },
            formatCurrency(val) {
                let currencyFormat = new Intl.NumberFormat('en-US')

                if (val) {
                    let amount = currencyFormat.format(val)

                    return '$' + amount
                }

                return '$0'
            },
            handleDateHover(label, day) {
                this.$set(day, 'hover', true)
                this.$set(day, 'showBubble', true)
            },
            handleDateUnhover(label, day) {
                this.$set(day, 'hover', false)
            },
            handleDateClick(label, day) {
                if (this.subscriptionWarning) {
                    this.$emit('show-subscription-warning')
                } else if (this.artistCalendar) {
                    this.handleArtistCalendarClick(label, day)
                } else {
                    this.handleVenueCalendarClick(label, day)
                }
            },
            handleArtistCalendarClick(label, day) {
                if (!day.markers) this.$set(day, 'markers', [])

                const markers = day.markers.filter((m) => m.type !== 'artistNotAvail')

                if (markers.length === 1) {
                    this.handleRedirectToEventPages(day, markers[0])
                } else if (!markers.length) {
                    this.handleMarkUnavails(label, day)
                }
            },
            handleVenueCalendarClick(label, day) {
                const date = new Date(`${day.day} ${label}`)
                const options = { month: 'long', day: 'numeric', year: 'numeric' }
                const formattedDate = date
                    .toLocaleDateString('en-US', options)
                    .replace(',', '')

                if (!day.markers || !day.markers.length) {
                    this.$emit('formatted-date', this.resolveFormattedDate(formattedDate))
                    this.$emit('show-modal')
                } else if (day.markers.length === 1) {
                    if (day.availsChecked) {
                        this.$emit('formatted-date', this.resolveFormattedDate(formattedDate))
                        this.$emit(
                            'show-invite-modal',
                            day.markers[0].startDate,
                            day.markers[0].vibe,
                            day.markers[0].budget,
                            day.markers[0].id,
                            day.markers[0].startTime,
                            day.markers[0].privateOpportunity
                        )
                    } else {
                        this.handleRedirectToEventPages(day, day.markers[0])
                    }
                }
            },
            handleRedirectToEventPages(day, marker) {
                if (!day.url || !marker.id) return

                localStorage.clear()
                localStorage.setItem('eventId', marker.id)
                if (day.url) window.location.href = day.url
            },
            handleMarkUnavails(label, day) {
                let artistNotAvailMarker = day.markers.find(
                    (m) => m.value === 'artistNotAvail'
                )
                if (!artistNotAvailMarker) {
                    this.addNotAvailMarker(label, day)
                } else {
                    this.removeNotAvailMarker(artistNotAvailMarker, label, day)
                }
            },
            handleLeaveBubble(day) {
                this.$set(day, 'hover', false)
                this.$set(day, 'showBubble', false)
            },
            handleStatusClick(status) {
                if (status.len < 2) {
                    return
                } else {
                    this.$emit('filter-events', status.eventStatus)
                }
            },
            addNotAvailMarker(label, day) {
                day.markers.push({
                    type: 'artistNotAvail',
                    value: 'artistNotAvail'
                })
                this.$set(day, 'artistNotAvail', true)

                this.$emit('add-unavail', this.convertLabelToDate(label, day))
            },
            removeNotAvailMarker(artistNotAvailMarker, label, day) {
                day.markers.splice(day.markers.indexOf(artistNotAvailMarker), 1)
                this.$set(day, 'artistNotAvail', false)

                this.$emit('remove-unavail', this.convertLabelToDate(label, day))
            },
            convertDateToLabel(date) {
                let year = date.getFullYear()
                let month = date.getMonth()

                return `${MONTHS[month]} ${year}`
            },
            convertLabelToDate(label, day) {
                let year = label.split(' ')[1]
                let month = Object.keys(MONTHS).find(
                    (key) => MONTHS[key] === label.split(' ')[0]
                )

                return new Date(year, month, day.day)
            },
            convertDateToDayObj(date) {
                let label = this.convertDateToLabel(date)
                let day = date.getDate()

                return this.calendar
                    .find((month) => month.label === label)
                    .days.find((d) => d.day === day)
            }
        }
    }
</script>
<style lang="scss" scoped>
    $smallMobile: 350px;
    @media screen and (max-width: $smallMobile) {
        .month-event-label {
            .label {
                font-size: 14px !important;
            }
            .events,
            .budget {
                font-size: 8px !important;
            }
        }
        .main-availsChecked-circle,
        .main-book-circle,
        .main-advance-circle,
        .main-wrapUp-circle,
        .main-cancelled-circle,
        .main-completed-circle,
        .current-circle {
            width: 24px !important;
            height: 24px !important;
            background-size: 24px 24px !important;
        }
        .main-border-cancelled-circle {
            width: 26px !important;
            height: 26px !important;
            background-size: 26px 26px !important;
        }
        .availsChecked-circle,
        .book-circle,
        .advance-circle,
        .wrapUp-circle,
        .completed-circle {
            width: 24px !important;
            height: 24px !important;
            clip-path: ellipse(24px 24px at -8px 14px) !important;
        }

        .main-cancelled-circle,
        .cancelled-circle {
            width: 26px !important;
            height: 26px !important;
            border-radius: 50%;
            background: url(/images/icons/booked-purple.svg) no-repeat;
            background-size: 26px 26px !important;
        }
        .artistNotAvail-circle {
            width: 26px !important;
            height: 26px !important;
            border-radius: 50%;
            background-size: 26px 26px !important;
        }
    }
    .mobile-venue-booking-calendar-container {
        position: relative;

        .bubble {
            width: fit-content;
            background: #464646;
            position: absolute;
            bottom: 75%;
            cursor: pointer;
            border-radius: 8px;
            z-index: 1;
            box-shadow: 0px 5px 20px 0px rgba(0, 0, 0, 0.2);
            padding: 8px 10px 10px 10px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-start;
            gap: 6px;

            .bubble-content {
                display: flex;
                flex-direction: row;
                justify-content: center;
                align-items: center;
                gap: 4px;
                z-index: 2;

                p {
                    margin-bottom: 0;
                }

                .status-len {
                    border-radius: 100px;
                    border: 1.5px solid white;
                    font-family: Instrument Sans;
                    font-size: 12px;
                    font-style: normal;
                    font-weight: 700;
                    line-height: 135%;
                    letter-spacing: 0.12px;
                    height: 24px;
                    width: 24px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }
                .status-value {
                    font-family: Instrument Sans;
                    font-size: 16px;
                    font-style: normal;
                    font-weight: 550;
                    line-height: 125%;
                    text-wrap: nowrap;
                }
            }
        }
        .bubble:before {
            content: '';
            position: absolute;
            top: 90%;
            left: 50%;
            height: 20px;
            width: 20px;
            background: #464646;
            transform: rotate(45deg) translate(-50%);
            border-bottom: inherit;
            border-right: inherit;
        }
        .bubble:after {
            content: '';
            position: absolute;
            top: 46%;
            left: 38%;
            height: 100%;
            width: 48px;
            background: transparent;
        }

        .month-event-label {
            display: flex;
            flex-direction: row;
            justify-content: flex-start;
            align-items: center;
            gap: 12px;
            max-width: 300px;
            overflow-x: hidden;

            p {
                margin-bottom: 0px;
            }

            .label {
                color: #dedede;
                font-family: Instrument Sans;
                font-size: 18px;
                font-style: normal;
                font-weight: 500;
                line-height: 120%;
                letter-spacing: -0.22px;
                text-wrap: nowrap;
            }

            .events,
            .budget {
                color: #646464;
                font-family: Instrument Sans;
                font-size: 12px;
                font-style: normal;
                font-weight: 550;
                line-height: 125%;
                text-wrap: nowrap;
            }
        }
        .color-circles {
            display: flex;
            flex-direction: row;
            position: absolute;
            top: 27%;
            right: 2%;

            .main-availsChecked-circle,
            .main-book-circle,
            .main-advance-circle,
            .main-wrapUp-circle,
            .main-cancelled-circle,
            .main-border-cancelled-circle,
            .main-completed-circle,
            .current-circle {
                width: 32px;
                height: 32px;
                border-radius: 50%;
            }
            .main-availsChecked-circle {
                border: 1.5px solid #cdeb09;
            }
            .main-book-circle {
                border: 1.5px solid #c9cd00;
            }
            .main-advance-circle {
                border: 1.5px solid #cb9b1f;
            }
            .main-wrapUp-circle {
                border: 1.5px solid #d188bf;
            }
            .main-completed-circle {
                border: 1.5px solid #646464;
            }
            .main-border-cancelled-circle {
                border: 1.5px solid #ba87ff;
                width: 32px;
                height: 32px;
                border-radius: 50%;
                background: url(/images/icons/booked-purple.svg) no-repeat;
                background-size: 32px 32px;
            }
            .availsChecked-circle,
            .book-circle,
            .advance-circle,
            .wrapUp-circle,
            .completed-circle {
                position: absolute;
                width: 32px;
                height: 32px;
                border-radius: 50%;
                clip-path: ellipse(32px 32px at -11px 20px);
            }

            .availsChecked-circle {
                border: 1.5px solid #cdeb09;
            }
            .book-circle {
                border: 1.5px solid #c9cd00;
            }
            .advance-circle {
                border: 1.5px solid #cb9b1f;
            }
            .wrapUp-circle {
                border: 1.5px solid #d188bf;
            }
            .completed-circle {
                border: 1.5px solid #646464;
            }
            .current-circle {
                border: 1.5px solid #dedede;
            }
            .main-cancelled-circle,
            .cancelled-circle {
                width: 32px;
                height: 32px;
                border-radius: 50%;
                background: url(/images/icons/booked-purple.svg) no-repeat;
                background-size: 32px 32px;
            }
            .artistNotAvail-circle {
                width: 32px;
                height: 32px;
                border-radius: 50%;
                background: url(/images/icons/booked-grey.svg) no-repeat;
                background-size: 32px 32px;
            }
        }
        .paginate-actions {
            display: flex;
            flex-direction: row;
            justify-content: space-between;

            .next-months {
                width: 24px;
                height: 100%;
                position: absolute;
                top: 0px;
                right: 0;
                cursor: pointer;
                display: flex;
                flex-direction: column;
                justify-content: flex-end;
                background: transparent;
            }

            .next-months {
                width: 24px;
                height: 10%;
                position: absolute;
                top: 0px;
                right: -4px;
                cursor: pointer;
                display: flex;
                flex-direction: column;
                justify-content: flex-end;
                background: transparent;
            }
            .next-months::before {
                content: url('/images/icons/next.svg');
            }

            .prev-months {
                width: 24px;
                height: 10%;
                position: absolute;
                top: 0px;
                left: -4px;
                cursor: pointer;
                display: flex;
                flex-direction: column;
                justify-content: flex-end;
                background: transparent;
            }
            .prev-months::before {
                content: url('/images/icons/prev.svg');
            }
        }
    }
</style>
